import type { DailySalesDay, SalesTotals } from '../../../../api/daily-sales';
import {
  addTotals,
  countNoun,
  dayTotal,
  formatCount,
  formatMoney,
  shortDate,
  totalsOf,
  weekdayName,
  ZERO,
  type SourceKey,
} from '../daily-sales-format';

interface LedgerRow {
  key: string;
  label: string;
  partial: boolean;
  cells: string[];
}

interface Column {
  heading: string;
  cell: (t: SalesTotals) => string;
}

const bookings = (heading: string): Column => ({
  heading,
  cell: (t) => formatCount(t.bookings),
});
const units = (heading: string): Column => ({ heading, cell: (t) => formatCount(t.tickets) });
const gross: Column = { heading: 'Gross', cell: (t) => formatMoney(t.grossCents) };

const GROUPS: Record<SourceKey, { name: string; columns: Column[] }> = {
  fareharbor: { name: 'FareHarbor', columns: [bookings('Bookings'), units('Tickets'), gross] },
  giftCards: { name: 'Gift cards', columns: [units('Sold'), gross] },
  nebari: { name: 'Nebari', columns: [bookings('Bookings'), units('Tickets'), gross] },
  giftPasses: { name: 'Gift passes', columns: [bookings('Orders'), units('Passes'), gross] },
  retailInStore: {
    name: 'Retail in store',
    columns: [bookings('Orders'), units('Items'), gross],
  },
  retailOnline: { name: 'Retail online', columns: [bookings('Orders'), units('Items'), gross] },
};

function capitalised(text: string): string {
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

function cellsFor(day: DailySalesDay, sources: SourceKey[]): string[] {
  const perSource = sources.flatMap((s) => GROUPS[s].columns.map((c) => c.cell(totalsOf(day, s))));
  if (sources.length < 2) return perSource;
  return [
    ...perSource,
    formatCount(dayTotal(day, 'bookings', sources)),
    formatMoney(dayTotal(day, 'grossCents', sources)),
  ];
}

function rowFor(day: DailySalesDay, today: string, sources: SourceKey[]): LedgerRow {
  const partial = day.date === today;
  return {
    key: day.date,
    label: `${weekdayName(day.date)} ${shortDate(day.date)}${partial ? ' (so far)' : ''}`,
    partial,
    cells: cellsFor(day, sources),
  };
}

function totalRow(days: DailySalesDay[], sources: SourceKey[]): LedgerRow {
  const sum = (source: SourceKey): SalesTotals =>
    days.map((d) => totalsOf(d, source)).reduce(addTotals, ZERO);
  const window: DailySalesDay = {
    date: 'total',
    fareharbor: sum('fareharbor'),
    giftCards: sum('giftCards'),
    nebari: sum('nebari'),
    giftPasses: sum('giftPasses'),
    retailInStore: sum('retailInStore'),
    retailOnline: sum('retailOnline'),
  };
  return { key: 'total', label: 'Total', partial: false, cells: cellsFor(window, sources) };
}

interface DailySalesLedgerProps {
  days: DailySalesDay[];
  today: string;
  sources: SourceKey[];
}

export function DailySalesLedger({ days, today, sources }: DailySalesLedgerProps) {
  const rows = [...days].reverse().map((d) => rowFor(d, today, sources));
  const total = totalRow(days, sources);
  const groups = sources.map((s) => ({ name: GROUPS[s].name, columns: GROUPS[s].columns }));
  if (sources.length > 1) {
    groups.push({
      name: 'All',
      columns: [bookings(capitalised(countNoun(sources))), gross],
    });
  }

  return (
    <section className="nb-card nb-ds-ledger" aria-label="Sales by day">
      <table className="nb-tbl nb-tabular">
        <thead>
          <tr>
            <th rowSpan={2} scope="col">
              Day
            </th>
            {groups.map((g) => (
              <th key={g.name} colSpan={g.columns.length} scope="colgroup" className="nb-ds-group">
                {g.name}
              </th>
            ))}
          </tr>
          <tr>
            {groups.flatMap((g) =>
              g.columns.map((c) => (
                <th key={`${g.name}-${c.heading}`} scope="col">
                  {c.heading}
                </th>
              )),
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={r.partial ? 'nb-ds-partial' : undefined}>
              <th scope="row">{r.label}</th>
              {r.cells.map((c, i) => (
                <td key={i}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="nb-ds-total">
            <th scope="row">{total.label}</th>
            {total.cells.map((c, i) => (
              <td key={i}>{c}</td>
            ))}
          </tr>
        </tfoot>
      </table>
    </section>
  );
}
